import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicExhibitionItem,
  PublicSessionItem,
  Portfolio,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Link, Text, View, Image } from '@react-pdf/renderer';
import { useCallback } from 'react';

import { adminEventsRouteFormat } from '@constants/paths';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'styled-components';
import { APP_HOST } from '@utils/apiHooks';
import PartnersAndExhibitorsPage from './pages/PartnersAndExhibitorsPage';

import { styles } from './styles';
import { getImageSrc } from '@utils/fn';

import TitlePage from './pages/TitlePage';
import ContactPage from './pages/ContactPage';
import { Ticket } from '@composites/Tickets';

import IntermediatePage from './pages/IntermediatePage';
import { SafeImage } from './components/SafeImage';
import { resolveImageColor } from '../BusinessPartnerPdfContent';
import CitationPage from './pages/CitationPage';
import PartnershipOpportunityPage from './pages/PartnershipOpportunityPage';
import BoostYourRevenuePage from './pages/BoostYourRevenuePage';
import WhoAndWhyPage from './pages/WhoAndWhyPage';
import PartnersAndExhibitorsDefaultPage from './pages/PartnersAndExhibitorsDefaultPage';
import EventLocationPage from './pages/EventLocationPage';
import KeyTopicsPage from './pages/KeyTopicsPage';
import SpeakersAndModeratorsPage from './pages/SpeakersAndModeratorsPage';
import AgendaPages from './pages/AgendaPages';
import { SPONSOR_EMAIL } from '@utils/constants';

interface AgendaPdfContentProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  theme: DefaultTheme;
  groupedAgendasSessions: PublicSessionItem[][];
  agendaPdfDates: string[];
  agendaSpeakers: AgendaSpeaker[];
  exhibitions: PublicExhibitionItem[];
  tickets: Ticket[];
  advertisements: any[] | undefined;
}

const AgendaPdfContent = ({
  event,
  coLocatedEvents,
  portfolios,
  groupedAgendasSessions,
  agendaPdfDates,
  agendaSpeakers,
  exhibitions,
  tickets,
  advertisements,
}: AgendaPdfContentProps) => {
  const { t } = useTranslation();
  const portfolioSlug = event?.portfolio?.slug || '';
  const portfolio = portfolios?.data.find(p => p.slug === portfolioSlug);
  const portfolioLogoSrc = getImageSrc(
    (portfolio?.design as any)?.logoPosImagePng || ''
  );

  const Header = useCallback(
    ({ event }: { event: PublicEventDetailsResponse }) => {
      const eventHomePageUrl = `${APP_HOST}/events/${event?.slug}`;
      const bookNowUrl = `${APP_HOST}/events/${event?.slug}${t('book-now-url')}`;

      const sponsorSubject = t('sponsorMailto.subject', {
        eventName: event?.name,
      });
      const sponsorBody = t('sponsorMailto.body', { eventName: event?.name });
      const sponsorMailHref = `mailto:${SPONSOR_EMAIL}?subject=${encodeURIComponent(sponsorSubject)}&body=${encodeURIComponent(sponsorBody)}`;

      return (
        <View style={styles.headerWrapper} fixed>
          <Link src={eventHomePageUrl} style={{ textDecoration: 'none' }}>
            <SafeImage
              src={getImageSrc(event?.images?.logo ?? '')}
              style={styles.eventLogo}
              debugLabel="event?.images?.logoTransparent"
            />
          </Link>

          <View style={styles.buttonsContainer}>
            <Link
              src={bookNowUrl}
              style={{
                ...styles.primaryButton,
                backgroundColor: event?.design?.themeColor || 'black',
                borderColor: event?.design?.themeColor || 'black',
              }}
            >
              <Text style={styles.primaryButtonText}>{t('book-now')}</Text>
            </Link>

            <Link
              src={sponsorMailHref}
              style={{
                ...styles.secondaryButton,
              }}
            >
              <Text
                style={{
                  ...styles.secondaryButtonText,
                  color: 'black',
                }}
              >
                {t('apply-as-partner-pdf')}
              </Text>
            </Link>
          </View>
        </View>
      );
    },
    []
  );

  const Footer = useCallback(
    ({ firstPage = false }: { firstPage?: boolean }) => {
      const eventLink = `${APP_HOST}${adminEventsRouteFormat(event?.slug, '')?.slice(0, -1)}
            `;
      return (
        <View
          style={{
            ...styles.footer,
            paddingHorizontal: firstPage ? 75 : 40,
          }}
          fixed
        >
          <View>
            {portfolioLogoSrc && (
              <Link
                href={`${APP_HOST}${t('mappable-company-routes.our-product-portfolio-url')}/${portfolio?.slug ?? ''}`}
                style={{ textDecoration: 'none' }}
              >
                <Image
                  style={styles.logo}
                  src={resolveImageColor(portfolioLogoSrc, 'black')}
                />
              </Link>
            )}
          </View>

          <View>
            <Text
              style={{
                ...styles.heartIcon,
              }}
            >
              {t('agenda-pdf-footer-text-1')} {'\uf004'}{' '}
              {t('agenda-pdf-footer-text-2')}
            </Text>
          </View>

          <View>
            <Link
              src={eventLink}
              style={{
                ...styles.eventName,
                color: event?.design?.themeColor ?? 'black',
                textDecoration: 'none',
              }}
            >
              {t('visit-the-website-here')}
            </Link>
          </View>

          {!firstPage && (
            <View>
              <Text
                style={{
                  ...styles.pageNr,
                  color: '#27343d',
                }}
                render={({ pageNumber }) => {
                  if (pageNumber > 1) {
                    return `${pageNumber - 1}`;
                  }
                  return '';
                }}
                fixed
              />
            </View>
          )}
        </View>
      );
    },
    [event?.design?.themeColor, event?.slug, t, portfolio, portfolioLogoSrc]
  );

  const errorHandlerWrapper = (
    label: string,
    renderFn: () => JSX.Element | null
  ) => {
    try {
      console.log(`Rendering ${label}`);
      return renderFn();
    } catch (err) {
      console.error(`Error rendering ${label}:`, err);
      return null;
    }
  };

  return (
    <Document>
      {errorHandlerWrapper('Title Page', () => (
        <TitlePage
          event={event}
          coLocatedEvents={coLocatedEvents}
          footer={<Footer firstPage />}
        />
      ))}

      {errorHandlerWrapper('Citation Page', () => (
        <CitationPage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('WhoAndWhyPage', () => (
        <WhoAndWhyPage
          event={event}
          portfolio={portfolio}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('Key Topics', () => (
        <KeyTopicsPage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('SpeakersAndModerators', () => (
        <SpeakersAndModeratorsPage
          event={event}
          agendaSpeakers={agendaSpeakers}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('AgendaPages', () => (
        <AgendaPages
          event={event}
          agendaPdfDates={agendaPdfDates}
          groupedAgendasSessions={groupedAgendasSessions}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('IntermediatePage', () => (
        <IntermediatePage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('PartnersAndExhibitors', () => (
        <PartnersAndExhibitorsDefaultPage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('PartnersAndExhibitors', () => (
        <PartnersAndExhibitorsPage
          event={event}
          exhibitions={exhibitions}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('Partnership Opportunity Page', () => (
        <PartnershipOpportunityPage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('Boost Your Revenue Page', () => (
        <BoostYourRevenuePage
          event={event}
          coLocatedEvents={coLocatedEvents}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {/*
      {errorHandlerWrapper('IncreaseYourSales', () => (
        <IncreaseYourSales
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {errorHandlerWrapper('ColocatedEvents', () =>
        event?.colocated ? (
          <ColocatedEvents
            event={event}
            coLocatedEvents={coLocatedEvents}
            portfolios={portfolios}
            header={<Header event={event} />}
            footer={<Footer />}
          />
        ) : null
      )}

      {errorHandlerWrapper('MatchMakingOne2OneMeetings', () => (
        <MatchMakingOne2OneMeetingsPage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}
      */}

      {errorHandlerWrapper('EventLocationPage', () => (
        <EventLocationPage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}

      {/*
      {errorHandlerWrapper('BookNow', () => (
        <BookNowPage
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}
      */}

      {errorHandlerWrapper('Contact', () => (
        <ContactPage
          event={event}
          header={<Header event={event} />}
          footer={<Footer />}
        />
      ))}
    </Document>
  );
};

export default AgendaPdfContent;
